class RelatedProductButtons extends HTMLElement {
    static htmlCache = new Map();

    constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this._switchTimer = null;
    }

    normalizeUrl(handle) {
        const { pathname, search } = new URL(handle, window.location.origin);
        return `${pathname}${search}`;
    }

    async fetchWithCache(url) {
        const key = this.normalizeUrl(url);

        if (RelatedProductButtons.htmlCache.has(key)) {
            return RelatedProductButtons.htmlCache.get(key);
        }

        const promise = fetch(key)
            .then(response => response.text())
            .then(text => new DOMParser().parseFromString(text, 'text/html'));

        RelatedProductButtons.htmlCache.set(key, promise);
        return promise;
    }

    connectedCallback() {
        this.buttons = this.querySelectorAll('.tab-selector__tab');
        this._suppressEventForIndex = null;

        this.buttons.forEach(button => {
            this.fetchWithCache(button.dataset.productHandle);
        });

        this.addEventListener('tab-change', (e) => {
            const tab = this.buttons[e.detail.index];
            if (!tab) return;

            const skipEvent = this._suppressEventForIndex === e.detail.index;
            this._suppressEventForIndex = null;

            clearTimeout(this._switchTimer);
            this._apoRetrying = false;
            this._switchTimer = setTimeout(() => {
                this.loadCustomizedProduct(tab.dataset.productHandle, this.sectionId, tab.dataset.customized === 'true', skipEvent);
            }, 50);
        });

        this.listenForApoInit();
        this.waitForProductInfo().then(() => this.restoreFromUrl());
    }

    listenForApoInit() {
        window.addEventListener('apo:event', (e) => {
            if (e.detail.event === 'init::finish') {
                this._apoRetrying = false;
                this.hideSkeleton();
                return;
            }
            if (e.detail.event === 'initialize::no::options::for::product' && !this._apoRetrying) {
                this._apoRetrying = true;
                this.forceApoReinit();
            }
        });
    }

    async forceApoReinit() {
        const activeTab = this.querySelector('.tab-selector__tab--active');
        if (!activeTab || activeTab.dataset.customized !== 'true') return;

        const html = await this.fetchWithCache(activeTab.dataset.productHandle);
        const productScript = html.getElementById('mwsApoProduct');
        const inventoryScript = html.getElementById('mwsApoInventoryVariants');
        if (!productScript || !inventoryScript) return;

        // Remove APO's rendered container
        const apoContainer = document.querySelector('.mw-apo-container');
        if (apoContainer) apoContainer.remove();

        // Clean up APO's DOM stamps from previous init
        document.querySelectorAll('[data-mw-apo-init]').forEach(el => {
            el.removeAttribute('data-mw-apo-init');
            el.removeAttribute('data-mw-apo-process');
            el.removeAttribute('data-mw-apo-variant-id');
        });

        const apo = window.mwApoInit;
        apo.product = JSON.parse(productScript.innerHTML);
        apo.mws_inventory = JSON.parse(inventoryScript.innerHTML);
        delete apo.response;
        apo.apo.initialized = false;
        apo.is_used = false;

        this.showSkeleton();
        apo.apo.initialize(apo);
    }

    async loadCustomizedProduct(handle, sectionId, isCustomized = false, skipEvent = false) {
        const productInfo =
            document.querySelector(`product-info#MainProduct-${sectionId}`) ||
            document.querySelector('product-info[id^="MainProduct"]') ||
            document.querySelector('product-info');

        try {
            if (!productInfo || typeof productInfo.handleCustomizedProductUpdate !== 'function') {
                window.location.href = handle;
                return;
            }

            const productUrl = this.normalizeUrl(handle);
            const isOriginalProduct = productUrl.split('?')[0] === this.normalizeUrl(productInfo.dataset.url).split('?')[0];

            const html = await this.fetchWithCache(handle);

            if (isCustomized) {
                this.showSkeleton();
            } else {
                this.hideSkeleton();
            }

            productInfo.handleCustomizedProductUpdate(productUrl, isOriginalProduct)(html);
            this.swapPopupModals(html, productInfo);

            const newProductInfo =
                html.querySelector(`product-info[id^='MainProduct']`) ||
                html.querySelector('product-info');
            const selectedVariantEl = newProductInfo?.querySelector('variant-selects [data-selected-variant]');
            const relatedVariant = selectedVariantEl ? JSON.parse(selectedVariantEl.innerHTML)?.id : null;

            const newUrl = `${productUrl.split('?')[0]}${relatedVariant ? `?variant=${relatedVariant}` : ''}`;
            window.history.pushState({}, '', newUrl);

            if (!skipEvent) {
                if (isCustomized) {
                    this.firePayloadAfterApoInit(html, productUrl);
                } else {
                    this.firePayload(html, productUrl);
                }
            }
        } catch (error) {
            console.error('Product swap failed:', error);
        }
    }

    firePayload(html, productUrl) {
        const payload = this.buildDataLayerPayload(html, productUrl);
        if (payload) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(payload);
        }
    }

    firePayloadAfterApoInit(html, productUrl) {
        let fired = false;
        const fire = () => {
            if (fired) return;
            fired = true;
            window.removeEventListener('apo:event', handler);
            clearTimeout(timeoutId);
            this.firePayload(html, productUrl);
        };
        const handler = (e) => {
            if (e.detail.event === 'init::finish') fire();
        };
        window.addEventListener('apo:event', handler);
        const timeoutId = setTimeout(fire, 3000);
    }

    parsePriceText(text) {
        if (!text) return null;
        const match = text.match(/[\d.,]+/);
        if (!match) return null;
        const num = match[0];
        const lastDot = num.lastIndexOf('.');
        const lastComma = num.lastIndexOf(',');
        const lastSepIdx = Math.max(lastDot, lastComma);
        if (lastSepIdx === -1) {
            const parsed = parseFloat(num);
            return Number.isFinite(parsed) ? parsed : null;
        }
        const afterSep = num.length - lastSepIdx - 1;
        let parsed;
        if (afterSep === 1 || afterSep === 2) {
            const intPart = num.slice(0, lastSepIdx).replace(/[.,]/g, '');
            const decPart = num.slice(lastSepIdx + 1);
            parsed = parseFloat(`${intPart}.${decPart}`);
        } else {
            parsed = parseFloat(num.replace(/[.,]/g, ''));
        }
        return Number.isFinite(parsed) ? parsed : null;
    }

    readDisplayedPrice() {
        const productInfo =
            document.querySelector(`product-info#MainProduct-${this.sectionId}`) ||
            document.querySelector('product-info[id^="MainProduct"]') ||
            document.querySelector('product-info');
        if (!productInfo) return null;
        const candidates = productInfo.querySelectorAll('.price__container .price-item--regular, .price__regular .price-item--regular, .price-item--regular');
        for (const el of candidates) {
            if (el.closest('.apo-options-price')) continue;
            const parsed = this.parsePriceText(el.textContent);
            if (parsed && parsed > 0) return parsed;
        }
        return null;
    }

    buildDataLayerPayload(html, productUrl) {
        const productInfo =
            html.querySelector(`product-info[id^='MainProduct']`) ||
            html.querySelector('product-info');
        if (!productInfo) return null;

        let variant = null;
        const selectedVariantEl = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (selectedVariantEl) {
            try { variant = JSON.parse(selectedVariantEl.innerHTML); } catch (e) {}
        }
        if (!variant) {
            const apoScript = html.getElementById('mwsApoProduct');
            const formVariantId = html.querySelector('input[name="id"]')?.value;
            if (apoScript) {
                try {
                    const apo = JSON.parse(apoScript.innerHTML);
                    variant = apo.variants?.find(v => String(v.id) === String(formVariantId)) || apo.variants?.[0];
                } catch (e) {}
            }
        }
        if (!variant) return null;

        let ld = null;
        const ldEl =
            productInfo.querySelector('script[type="application/ld+json"]') ||
            html.querySelector('script[type="application/ld+json"]');
        if (ldEl) {
            try { ld = JSON.parse(ldEl.innerHTML); } catch (e) {}
        }

        const productId = productInfo.dataset.productId || '';
        const productName = ld?.name || '';
        const vendor = ld?.brand?.name || '';
        const variantPriceMajor = typeof variant.price === 'number' ? variant.price / 100 : 0;
        const displayedPrice = this.readDisplayedPrice();
        const priceMajor = displayedPrice != null ? displayedPrice : variantPriceMajor;
        const currency = window.Shopify?.currency?.active || 'USD';
        const itemUrl = this.normalizeUrl(productUrl).split('?')[0];

        return {
            event: 'view_item_dynamic',
            ecommerce: {
                value: String(priceMajor),
                currency,
                items: [{
                    item_id: String(productId),
                    item_sku: variant.sku || '',
                    item_variant: String(variant.id),
                    item_name: productName,
                    variant_name: variant.title || 'Default Title',
                    item_category: '',
                    price: priceMajor,
                    item_brand: vendor,
                    item_url: itemUrl,
                    quantity: '1'
                }]
            },
            ecomm_pagetype: 'product'
        };
    }

    restoreFromUrl() {
        const currentPath = window.location.pathname;

        const matchingButton = Array.from(this.buttons).find(button => {
            const buttonPath = this.normalizeUrl(button.dataset.productHandle).split('?')[0];
            return buttonPath === currentPath;
        });

        if (!matchingButton) return;

        const productInfo =
            document.querySelector(`product-info#MainProduct-${this.sectionId}`) ||
            document.querySelector('product-info[id^="MainProduct"]') ||
            document.querySelector('product-info');

        if (!productInfo) return;

        const productUrl = this.normalizeUrl(productInfo.dataset.url).split('?')[0];
        if (currentPath === productUrl) return;

        const tabSelector = this.querySelector('tab-selector');
        const index = Array.from(this.buttons).indexOf(matchingButton);
        if (tabSelector && index >= 0) {
            this._suppressEventForIndex = index;
            tabSelector.selectTab(index);
            this._suppressEventForIndex = null;
        }

        productInfo.activeProductUrl = this.normalizeUrl(matchingButton.dataset.productHandle);
    }

    swapPopupModals(html, productInfo) {
        const target = productInfo.parentElement;
        if (!target) return;

        document.querySelectorAll('modal-dialog.product-popup-modal').forEach(el => el.remove());
        html.querySelectorAll('modal-dialog.product-popup-modal').forEach(el => {
            target.appendChild(el.cloneNode(true));
        });
    }

    showSkeleton() {
        const skeleton = this.querySelector('.apo-skeleton');
        if (skeleton) skeleton.hidden = false;
    }

    hideSkeleton() {
        const skeleton = this.querySelector('.apo-skeleton');
        if (skeleton) skeleton.hidden = true;
    }

    waitForProductInfo() {
        return new Promise(resolve => {
            const productInfo =
                document.querySelector(`product-info#MainProduct-${this.sectionId}`) ||
                document.querySelector('product-info[id^="MainProduct"]') ||
                document.querySelector('product-info');

            if (productInfo) {
                resolve(productInfo);
            } else {
                document.addEventListener('product-info:loaded', () => resolve(
                    document.querySelector(`product-info#MainProduct-${this.sectionId}`) ||
                    document.querySelector('product-info[id^="MainProduct"]') ||
                    document.querySelector('product-info')
                ), { once: true });
            }
        });
    }
}

if (!customElements.get('related-product-buttons')) {
    customElements.define('related-product-buttons', RelatedProductButtons);
}
